/**
 * @file WsClientTransport — stateless typed WebSocket transport.
 *
 * Workspace Clients inject this via `deps.ws` and call
 * `transport.subscribe(SomeContract, args)` to get a fully-typed
 * `TypedSubscription<C, S>`. The transport owns URL assembly and
 * `ws://`/`wss://` scheme derivation; everything frame-level lives in
 * the `TypedSubscription` (Phase 2) it produces.
 *
 * mTLS (umbrella #1048 / #1050): when a `tls` bundle is supplied, the
 * transport builds an undici `Agent` carrying the client cert at
 * `onStart` and every `subscribe(...)` dials through undici's
 * `WebSocket` with that dispatcher — the same intra-cluster mTLS
 * posture `HttpClientTransport` gives `fetch`. Binary frames already
 * ride `TypedSubscription.sendBinary` / `onBinary` untouched, so a
 * typed subscription over a mutual-TLS hop carries opaque binary
 * payloads (the pty bridge's case) with no extra knobs. Outside Node
 * the `tls` prop is rejected loudly — browser WebSockets can't carry
 * application-supplied client certs.
 *
 * Phase 2b of umbrella #518 promotes this class from `CoreClass` onto
 * `CoreTransport extends CoreLifecycle` so it participates in the
 * parent's child-walk uniformly. WS subscriptions stay owned by the
 * per-call `TypedSubscription` instances `subscribe(...)` returns;
 * `onStop` does not tear them down — they're per-call and the caller
 * owns each one's `unsubscribe()`.
 *
 * Phase 4 of umbrella #406. Pairs with `HttpClientTransport` to give
 * workspace Clients a complete per-protocol transport layer.
 */

import {
  CoreError,
  CoreTransport,
  interpolate,
  type TlsProps,
} from '../../shared/index.ts'
import type { SubscriptionContract } from '../../shared/routing.ts'
import { TypedSubscription } from '../typed_subscription.ts'
import type {
  CreateWebSocket,
  WebSocketLike,
  WsSubscriptionProps,
} from '../ws_subscription.ts'

////////////////////////////////////////////////////////////////////////////////
// Types
////////////////////////////////////////////////////////////////////////////////

/**
 * Minimal undici.Dispatcher shape the transport holds between
 * `onStart` and `onStop`. Declared locally so this file doesn't
 * depend on undici's types when bundled for browser builds — the
 * dynamic `import('undici')` in `buildTlsFactory` is the only place
 * undici is named, and it never runs outside Node.
 */
type Dispatcher = {
  close?: () => Promise<void> | void
}

/**
 * Constructor props for `WsClientTransport`.
 *
 * @property id - Lifecycle entity id (default `'ws'`).
 * @property baseUrl - Base URL prepended to every subscription path.
 *   Accepts `http(s)://` (auto-rewritten to `ws(s)://`) or `ws(s)://`
 *   directly. Trailing slash stripped on assignment.
 * @property tls - mTLS cert/key/CA this transport presents on every
 *   subscription dial. Node-only: threaded into undici's `WebSocket`
 *   via a TLS-configured `Agent` dispatcher built at `onStart`.
 *   Browsers can't carry application-supplied client certs, so setting
 *   this outside Node fails loud at start.
 * @property createWebSocket - Optional socket factory override; wins
 *   over both the default global `WebSocket` and the `tls` dispatcher
 *   path. Test seams and exotic dialers use this.
 * @property initialReconnectDelay - Forwarded to `WsSubscription` on
 *   construction.
 * @property maxReconnectDelay - Forwarded to `WsSubscription`.
 * @property requestTimeout - Forwarded to `WsSubscription` (correlated
 *   request-response timeout).
 */
export type WsClientTransportProps = {
  id?: string
  baseUrl: string
  tls?: TlsProps
  createWebSocket?: CreateWebSocket
  initialReconnectDelay?: number
  maxReconnectDelay?: number
  requestTimeout?: number
}

/**
 * Per-call arguments for `subscribe(...)`. Mirrors `HttpClientTransport`'s
 * `RequestArgs` minus the body field — WS subscriptions don't have a
 * one-shot body; the `clientFrame` channel does that work.
 */
export type SubscribeArgs = {
  params?: Record<string, string>
  query?: Record<string, string | number | boolean | undefined>
}

////////////////////////////////////////////////////////////////////////////////
// WsClientTransport
////////////////////////////////////////////////////////////////////////////////

/**
 * Stateless WebSocket transport. Consumed by workspace Client classes
 * (`Lab`, `Vmm`, …) via constructor injection.
 *
 * @example
 * ```typescript
 * const ws = new WsClientTransport({ baseUrl: 'http://127.0.0.1:24300' })
 * const sub = ws.subscribe(TerminalSub, { params: { vmId: '…' } })
 * sub.onData(frame => render(frame))
 * sub.send({ type: 'resize', cols: 80, rows: 24 })
 * ```
 */
export class WsClientTransport extends CoreTransport<WsClientTransportProps> {
  //////////////////////////////////////////////////////////////////////////////
  // Instance Properties
  //////////////////////////////////////////////////////////////////////////////

  private baseUrl: string
  private subProps: Omit<WsSubscriptionProps, 'url' | 'createWebSocket'>
  private tls?: TlsProps
  private explicitFactory?: CreateWebSocket
  /**
   * TLS socket factory built at `onStart` when `tls` is configured —
   * dials undici's `WebSocket` through the mTLS `Agent` below. Null
   * until started (or when no `tls` is set).
   */
  private tlsFactory: CreateWebSocket | null = null
  /**
   * The undici `Agent` behind {@link tlsFactory}. Held so `onStop`
   * can close its pooled sockets.
   */
  private dispatcher: Dispatcher | null = null

  //////////////////////////////////////////////////////////////////////////////
  // Constructor
  //////////////////////////////////////////////////////////////////////////////

  /**
   * Synchronous construction. Validates `baseUrl` at call time so a
   * misconfigured daemon fails loud at boot. Stays in `created` state —
   * the lifecycle (`configure()` → `start()`) is driven by the
   * adopting parent's child-walk.
   */
  constructor(props: WsClientTransportProps) {
    super({ ...props, id: props.id ?? 'ws' })
    this.baseUrl = ''
    this.subProps = {}
    this.applyProps(props)
  }

  //////////////////////////////////////////////////////////////////////////////
  // Public Methods - toJSON
  //////////////////////////////////////////////////////////////////////////////

  override toJSON(): Record<string, unknown> {
    return {
      ...super.toJSON(),
      baseUrl: this.baseUrl,
      mtls: this.tls !== undefined,
      ...this.subProps,
    }
  }

  //////////////////////////////////////////////////////////////////////////////
  // Public Methods - Reconfigure
  //////////////////////////////////////////////////////////////////////////////

  /**
   * Update transport configuration on a live entity. Mirrors
   * `HttpClientTransport.reconfigure` — applies fields synchronously,
   * then re-runs `onConfigure` via `CoreLifecycle.reconfigure`. Legal
   * in `configured` and `running` states only.
   */
  async reconfigure(props: Partial<WsClientTransportProps>): Promise<void> {
    this.applyProps(props)
    await this.runReconfigure()
  }

  //////////////////////////////////////////////////////////////////////////////
  // Lifecycle
  //////////////////////////////////////////////////////////////////////////////

  /**
   * Build the mTLS socket factory when a `tls` bundle is configured.
   * Node-only: the dynamic `import('undici')` is `@vite-ignore`'d so
   * SPA bundlers don't tree-pull undici into browser bundles, and a
   * non-Node runtime with `tls` set fails loud here rather than
   * silently dialing without a client cert.
   */
  protected override async onStart(): Promise<void> {
    if (!this.tls) return
    if (!WsClientTransport.isNodeRuntime()) {
      throw new CoreError({
        code: 'WS_TLS_UNSUPPORTED_RUNTIME',
        message:
          'WsClientTransport tls requires Node (browser WebSockets cannot ' +
          'carry application-supplied client certificates)',
      })
    }
    const { factory, dispatcher } = await WsClientTransport.buildTlsFactory(
      this.tls,
    )
    this.tlsFactory = factory
    this.dispatcher = dispatcher
    this.info('start', { mtls: true })
  }

  /** Tear down the mTLS dispatcher so pooled sockets don't leak. */
  protected override async onStop(): Promise<void> {
    if (this.dispatcher?.close) {
      await this.dispatcher.close()
    }
    this.dispatcher = null
    this.tlsFactory = null
  }

  //////////////////////////////////////////////////////////////////////////////
  // Private Methods - props handling
  //////////////////////////////////////////////////////////////////////////////

  /** Apply props synchronously. Shared by constructor + reconfigure. */
  private applyProps(props: Partial<WsClientTransportProps>): void {
    if (props.baseUrl !== undefined) {
      let url: URL
      try {
        url = new URL(props.baseUrl)
      } catch {
        throw new CoreError({
          code: 'INVALID_CONFIG',
          message: `Invalid baseUrl: ${props.baseUrl}`,
          metadata: { baseUrl: props.baseUrl },
        })
      }
      // Coerce http(s) into ws(s) so callers can hand us the same URL
      // they hand the HttpClientTransport.
      if (url.protocol === 'http:') url.protocol = 'ws:'
      else if (url.protocol === 'https:') url.protocol = 'wss:'
      this.baseUrl = url.toString().replace(/\/$/, '')
    }
    if (props.tls !== undefined) {
      this.tls = props.tls
      // A live tls swap needs a fresh dispatcher; runReconfigure
      // re-runs onConfigure, and the next start rebuilds. Clearing
      // here keeps a stale-cert factory from outliving the swap.
      this.tlsFactory = null
    }
    if (props.createWebSocket !== undefined) {
      this.explicitFactory = props.createWebSocket
    }
    if (props.initialReconnectDelay !== undefined) {
      this.subProps.initialReconnectDelay = props.initialReconnectDelay
    }
    if (props.maxReconnectDelay !== undefined) {
      this.subProps.maxReconnectDelay = props.maxReconnectDelay
    }
    if (props.requestTimeout !== undefined) {
      this.subProps.requestTimeout = props.requestTimeout
    }
  }

  //////////////////////////////////////////////////////////////////////////////
  // Public Methods - Subscribe
  //////////////////////////////////////////////////////////////////////////////

  /**
   * Open a typed WebSocket subscription against a contract produced by
   * `defineSubscription(...)` (Phase 2). Path-param interpolation
   * matches `HttpClientTransport.request` (shared `interpolate` helper);
   * query params are appended URL-encoded.
   *
   * Returns a `TypedSubscription<C, S>` whose `send` / `onData` are
   * validated against the contract's `clientFrame` / `serverFrame`;
   * binary frames ride `sendBinary` / `onBinary` unvalidated.
   *
   * With a `tls` bundle configured, the transport must be **started**
   * before subscribing (the mTLS dispatcher is built in `onStart`);
   * subscribing earlier throws rather than dialing without the client
   * cert.
   *
   * @param contract - Subscription contract (`SubscriptionContract<...>`).
   * @param args - Per-call params / query.
   * @returns Typed subscription ready to attach handlers.
   */
  subscribe<P extends string, Params, Query, C, S>(
    contract: SubscriptionContract<P, Params, Query, C, S>,
    args: SubscribeArgs = {},
  ): TypedSubscription<C, S> {
    const createWebSocket = this.resolveFactory()
    const url = this.buildUrl(contract.path, args.params, args.query)
    this.debug('subscribe', { path: contract.path })
    return new TypedSubscription<C, S>(contract, {
      url,
      ...this.subProps,
      ...(createWebSocket ? { createWebSocket } : {}),
    })
  }

  //////////////////////////////////////////////////////////////////////////////
  // Private Methods
  //////////////////////////////////////////////////////////////////////////////

  /**
   * Pick the socket factory for a new subscription: explicit override
   * first, then the started mTLS factory, else `undefined` (global
   * `WebSocket`). Throws when `tls` is configured but the factory
   * isn't built yet — dialing an mTLS listener without the client cert
   * would just produce an opaque handshake failure downstream.
   */
  private resolveFactory(): CreateWebSocket | undefined {
    if (this.explicitFactory) return this.explicitFactory
    if (this.tls) {
      if (!this.tlsFactory) {
        throw new CoreError({
          code: 'WS_TLS_NOT_STARTED',
          message:
            'WsClientTransport has tls configured but is not started; ' +
            'start() builds the mTLS dispatcher before subscribe()',
        })
      }
      return this.tlsFactory
    }
    return undefined
  }

  private buildUrl(
    path: string,
    params?: Record<string, string>,
    query?: Record<string, string | number | boolean | undefined>,
  ): string {
    const interpolated = params ? interpolate(path, params) : path
    const url = new URL(`${this.baseUrl}${interpolated}`)
    if (query) {
      for (const [k, v] of Object.entries(query)) {
        if (v === undefined) continue
        url.searchParams.append(k, String(v))
      }
    }
    return url.toString()
  }

  /**
   * True if this process is running under Node.js. Browsers and edge
   * workers return false — there the `tls` prop is rejected instead of
   * silently ignored (unlike `HttpClientTransport`, whose browser
   * consumers legitimately rely on OS-keystore client certs; a WS
   * consumer that asked for mTLS and can't get it should fail loud).
   */
  private static isNodeRuntime(): boolean {
    return (
      typeof process !== 'undefined' &&
      (process as { versions?: { node?: string } }).versions?.node != null
    )
  }

  /**
   * Build the undici `WebSocket` factory dialing through a TLS
   * `Agent` configured with the supplied PEMs. Mirrors
   * `HttpClientTransport`'s dispatcher build: CA-signed-leaf trust
   * with hostname checks disabled (intra-cluster SANs are role
   * tokens, not the loopback/WG IPs actually dialed — see #356).
   */
  private static async buildTlsFactory(
    tls: TlsProps,
  ): Promise<{ factory: CreateWebSocket; dispatcher: Dispatcher }> {
    const { Agent, WebSocket: UndiciWebSocket } = await import(
      /* @vite-ignore */ 'undici'
    )
    const dispatcher = new Agent({
      connect: {
        cert: tls.cert,
        // `.expose()` at the TLS handshake boundary — `tls.key` is a
        // `CoreSecret` so it can't escape via accidental serialization;
        // plaintext only crosses into undici's TLS connect here.
        key: tls.key.expose(),
        ca: tls.ca,
        checkServerIdentity: () => undefined,
      },
    })
    const factory: CreateWebSocket = url =>
      new UndiciWebSocket(url, { dispatcher }) as unknown as WebSocketLike
    return { factory, dispatcher }
  }
}
